/**
 * Battery Bridge
 * 
 * Provides battery status information.
 * Full support on Chrome/Edge via Battery API.
 * Returns fallback values on unsupported browsers.
 */

import { BaseBridge } from '../BaseBridge'
import type { BridgeAvailability, BridgeCapabilities, BatteryStatus } from '../types'

// Battery API types (not in standard lib.dom.d.ts)
interface BatteryManager extends EventTarget {
  charging: boolean
  chargingTime: number
  dischargingTime: number
  level: number
}

declare global {
  interface Navigator {
    getBattery?: () => Promise<BatteryManager>
  }
}

export class BatteryBridge extends BaseBridge {
  readonly bridgeName = 'Battery'
  readonly availability: BridgeAvailability = 'full'
  readonly methods = ['getBatteryStatus']
  
  private battery: BatteryManager | null = null
  private supported = false
  
  async initialize(): Promise<void> {
    // Check if Battery API is available
    if ('getBattery' in navigator && navigator.getBattery) {
      try {
        this.battery = await navigator.getBattery()
        this.supported = true
      } catch {
        this.supported = false
      }
    }
  }
  
  async handleCall(method: string, _args: any[]): Promise<any> {
    switch (method) {
      case 'getBatteryStatus':
        return JSON.stringify(await this.getBatteryStatus())
      default:
        throw this.createMethodNotFoundError(method)
    }
  }
  
  private async getBatteryStatus(): Promise<BatteryStatus> {
    if (!this.supported || !this.battery) {
      // Return fallback for unsupported browsers
      return {
        level: 100,
        isCharging: true,
        chargingTime: null,
        dischargingTime: null
      }
    }
    
    return {
      level: Math.round(this.battery.level * 100),
      isCharging: this.battery.charging,
      chargingTime: this.battery.chargingTime === Infinity ? null : this.battery.chargingTime,
      dischargingTime: this.battery.dischargingTime === Infinity ? null : this.battery.dischargingTime
    }
  }
  
  getCapabilities(): BridgeCapabilities {
    return {
      getBatteryStatus: { 
        available: true,
        fallback: this.supported ? undefined : 'Returns default values (API not supported)'
      }
    }
  }
}

